import type { Arcana, CardId, CourtRank, Locale, Suit } from './types.js';
import {
  ACE_TERMS,
  NUMBER_WORDS,
  RANK_TERMS,
  ROMAN,
  STOPWORDS,
  SUIT_TERMS,
} from './vocab.js';

export interface SearchableCard {
  id: CardId;
  arcana: Arcana;
  suit: Suit;
  number: number;
  rank: CourtRank | null;
  sortOrder: number;
  /** Card name per locale, e.g. { en: 'The Moon', es: 'La Luna' }. */
  names: Partial<Record<Locale, string>>;
  keywords: Partial<Record<Locale, string[]>>;
}

interface IndexEntry {
  id: CardId;
  sortOrder: number;
  /** Words from the card's names, in every locale. Ranked highest. */
  nameTokens: string[];
  /** Full normalized names, for exact-match ranking. */
  fullNames: string[];
  keywordTokens: string[];
  /** Suit, rank, and number terms. */
  structuralTokens: string[];
}

export interface SearchIndex {
  entries: IndexEntry[];
}

/**
 * Fold to a comparable form: lowercase, strip accents, drop punctuation.
 * "Ángeles" and "angeles" must be the same string, or a Spanish speaker
 * typing without accents on a phone keyboard finds nothing.
 */
export function normalize(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function tokenize(input: string): string[] {
  const n = normalize(input);
  return n ? n.split(' ').filter((t) => t && !STOPWORDS.has(t)) : [];
}

function uniq(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))];
}

function structuralTermsFor(card: SearchableCard): string[] {
  const terms: string[] = [];

  for (const locale of ['en', 'es'] as const) {
    terms.push(...SUIT_TERMS[card.suit][locale].flatMap(tokenize));
    if (card.rank) terms.push(...RANK_TERMS[card.rank][locale].flatMap(tokenize));
    if (card.arcana === 'minor' && card.number === 1) {
      terms.push(...ACE_TERMS[locale].flatMap(tokenize));
    }
    const word = NUMBER_WORDS[locale][card.number];
    if (word) terms.push(normalize(word));
  }

  // Digit and roman forms. Prefix matching keeps "3" away from "13":
  // "13".startsWith("3") is false, so a query of "3" hits exactly the
  // Empress and the four threes.
  terms.push(String(card.number));
  const roman = ROMAN[card.number];
  if (roman) terms.push(roman);

  return uniq(terms);
}

export function buildSearchIndex(cards: SearchableCard[]): SearchIndex {
  const entries = cards.map<IndexEntry>((card) => {
    const names = Object.values(card.names).filter(Boolean) as string[];
    const keywords = Object.values(card.keywords).flat().filter(Boolean) as string[];

    return {
      id: card.id,
      sortOrder: card.sortOrder,
      nameTokens: uniq(names.flatMap(tokenize)),
      fullNames: uniq(names.map(normalize)),
      keywordTokens: uniq(keywords.flatMap(tokenize)),
      structuralTokens: structuralTermsFor(card),
    };
  });

  return { entries };
}

/**
 * Very short queries do not get to match keyword prefixes.
 *
 * Without this, typing "as" (looking for the Aces) also drags in every card
 * whose keywords contain "assessment", and the useful results are buried
 * under noise before the user has finished the word. Names and structural
 * terms still match at any length, so "3" and "as" behave exactly as the
 * SPEC describes.
 */
const MIN_KEYWORD_PREFIX = 3;

/** Score a single query term against one card. 0 means no match. */
function scoreTerm(entry: IndexEntry, term: string): number {
  if (entry.fullNames.some((n) => n === term)) return 100;
  if (entry.nameTokens.some((t) => t === term)) return 60;
  if (entry.nameTokens.some((t) => t.startsWith(term))) return 40;
  if (entry.structuralTokens.some((t) => t === term)) return 30;
  if (entry.structuralTokens.some((t) => t.startsWith(term))) return 20;
  if (entry.keywordTokens.some((t) => t === term)) return 15;
  if (term.length >= MIN_KEYWORD_PREFIX && entry.keywordTokens.some((t) => t.startsWith(term))) {
    return 8;
  }
  return 0;
}

/**
 * Filter cards by a raw, un-submitted query string.
 *
 * Every term must match something (AND), so "three cups" narrows rather than
 * widens.
 *
 * Results come back in the deck's own order, not by relevance. This drives a
 * carousel the user swipes through, and a rail that reshuffles itself between
 * keystrokes is disorienting in a way a ranked list of links is not. Deck
 * order also matches the SPEC's worked example: filtering on "3" puts the
 * Empress (III) first, ahead of the four threes. Scoring still decides what
 * matches — it just does not decide what comes first.
 */
export function searchCards(index: SearchIndex, query: string): CardId[] {
  const terms = tokenize(query);

  if (terms.length === 0) {
    return [...index.entries]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((e) => e.id);
  }

  const matches: { id: CardId; sortOrder: number }[] = [];

  for (const entry of index.entries) {
    const matchedAll = terms.every((term) => scoreTerm(entry, term) > 0);
    if (matchedAll) matches.push({ id: entry.id, sortOrder: entry.sortOrder });
  }

  matches.sort((a, b) => a.sortOrder - b.sortOrder);
  return matches.map((m) => m.id);
}
